import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FiDollarSign, FiCalendar, FiSearch, FiDownload, FiPrinter, FiCheckCircle,FiUser, FiBook, FiFilter, FiArrowRight } from 'react-icons/fi';
import Card from '../../components/Card/Card';
import Badge from '../../components/Badge/Badge';
import Button from '../../components/Button/Button';
import Input from '../../components/Input/Input';
import Select from '../../components/Select/Select';
import DataTable from '../../components/DataTable/DataTable';
import api from '../../api';
import './Collection.css';

const Collection = () => {
  const [data, setData] = useState([]);
  const [stats, setStats] = useState({ today: 0, week: 0, month: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [range, setRange] = useState('month');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'PaymentDate', direction: 'desc' });

  const fetchCollections = async () => {
    setLoading(true);
    try {
      let url = `/collection.php?range=${range}&search=${search}`;
      if (range === 'custom') {
        url += `&start=${customRange.start}&end=${customRange.end}`;
      }
      const res = await api.get(url);
      const result = res.data;
      setData(Array.isArray(result.collections) ? result.collections : []);
      setStats(result.stats || { today: 0, week: 0, month: 0, total: 0 });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollections();
  }, [range, customRange, search]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key] || '';
      const bVal = b[sortConfig.key] || '';
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig]);

  const handleExportCSV = () => {
    const headers = ["Payment Date", "Member", "ID", "Roll No", "Book", "Acc No", "Amount", "Remarks"];
    const rows = sortedData.map(c => [
      new Date(c.PaymentDate).toLocaleString(),
      c.MemberName,
      c.MemberId,
      c.RollNo,
      c.BookTitle,
      c.AccessionNo,
      c.Amount,
      c.Remarks
    ]);
    
    let csvContent = "data:text/csv;charset=utf-8," 
      + headers.join(",") + "\n"
      + rows.map(r => r.map(v => `"${v}"`).join(",")).join("\n");
      
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Collection_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    window.print();
  };

  const columns = [
    { key: 'PaymentDate', label: 'Reference', sortable: true, render: (val, row) => {
      const d = new Date(val);
      const formatted = `${d.getDate().toString().padStart(2, '0')} ${d.toLocaleString('en-GB', { month: 'short' })}, ${d.getFullYear()}`;
      return (
        <div className="collection-ref">
          <p className="pay-date">{formatted}</p>
          <p className="pay-id">REF#{row.CollectionId}</p>
        </div>
      );
    }},
    { key: 'MemberName', label: 'Member Source', sortable: true, render: (val, row) => (
      <div className="source-cell">
        <FiUser className="cell-icon" />
        <div>
          <p className="source-name">{val}</p>
          <p className="source-meta">{row.RollNo} | {row.Department || row.MemberType}</p>
        </div>
      </div>
    )},
    { key: 'BookTitle', label: 'Item/Book', render: (val, row) => (
      <div className="source-cell">
        <FiBook className="cell-icon cell-icon--book" />
        <div>
          <p className="source-name source-name--book">{val}</p>
          <p className="source-meta">Acc: {row.AccessionNo}</p>
        </div>
      </div>
    )},
    { key: 'Amount', label: 'Amount Collected', sortable: true, render: (val) => (
      <div className="amount-cell">
        <span className="currency">₹</span>
        <span className="value">{parseFloat(val).toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
      </div>
    )},
    { key: 'Remarks', label: 'Remarks', render: (val) => <span className="remarks-text">{val || '—'}</span> }
  ];

  return (
    <div className="collection-page">
      {/* ── Page Header ── */}
      <div className="page-header">
        <div>
          <h2 className="page-header__title">Revenue Collections</h2>
          <p className="page-header__sub">Track library fines and collection history</p>
        </div>
        <div className="header-actions">
          <Button variant="secondary" icon={<FiDownload />} onClick={handleExportCSV}>Export CSV</Button>
          <Button variant="primary" icon={<FiPrinter />} onClick={handlePrint}>Print Report</Button>
        </div>
      </div>

      {/* ── Stats Row ── */}
      <div className="collection-stats">
        <Card className="stat-card stat-card--today">
          <div className="stat-card__icon"><FiDollarSign /></div>
          <div><p className="stat-card__label">Today's Collection</p><p className="stat-card__value">₹{stats.today.toLocaleString()}</p></div>
        </Card>
        <Card className="stat-card stat-card--week">
          <div className="stat-card__icon"><FiCalendar /></div>
          <div><p className="stat-card__label">Weekly Total</p><p className="stat-card__value">₹{stats.week.toLocaleString()}</p></div>
        </Card>
        <Card className="stat-card stat-card--month">
          <div className="stat-card__icon"><FiFilter /></div>
          <div><p className="stat-card__label">Monthly Target</p><p className="stat-card__value">₹{stats.month.toLocaleString()}</p></div>
        </Card>
        <Card className="stat-card stat-card--total">
          <div className="stat-card__icon"><FiCheckCircle /></div>
          <div><p className="stat-card__label">Lifetime Collection</p><p className="stat-card__value">₹{stats.total.toLocaleString()}</p></div>
        </Card>
      </div>

      {/* ── Main content ── */}
      <Card padding="none">
        <div className="shared-toolbar coll-toolbar">
          <div className="shared-search-box">
            <FiSearch className="shared-search-box__icon" />
            <input 
              type="text" 
              placeholder="Search member, ID, roll no, or book..." 
              value={search} 
              onChange={(e) => setSearch(e.target.value)} 
              className="shared-search-box__input" 
            />
          </div>
          
          <div className="coll-filters">
            <Select 
              options={[
                {value:'all', label:'All Records'},
                {value:'today', label:'Today'},
                {value:'week', label:'Last 7 Days'},
                {value:'month', label:'This Month'},
                {value:'year', label:'This Year'},
                {value:'custom', label:'Custom Range'}
              ]} 
              value={range} 
              onChange={(e) => setRange(e.target.value)}
              className="coll-select"
            />
            {range === 'custom' && (
              <div className="custom-range-box">
                <input type="date" value={customRange.start} onChange={(e) => setCustomRange(p=>({...p, start:e.target.value}))} />
                <FiArrowRight />
                <input type="date" value={customRange.end} onChange={(e) => setCustomRange(p=>({...p, end:e.target.value}))} />
              </div>
            )}
          </div>
        </div>
        
        <DataTable columns={columns} data={sortedData} loading={loading} onSort={handleSort} sortConfig={sortConfig} />
      </Card>

      {/* ── Print Secret Component ── */}
      <div className="print-report-container">
        <div className="print-header">
          <h1>NATIONAL INSTITUTE OF TECHNOLOGY</h1>
          <p>Central Library Management System - Revenue Report</p>
          <div className="print-meta">
            <div><strong>Report Period:</strong> {range.toUpperCase()} {range === 'custom' ? `(${customRange.start} to ${customRange.end})` : ''}</div>
            <div><strong>Generated At:</strong> {new Date().toLocaleString()}</div>
          </div>
        </div>
        
        <table className="print-table">
          <thead>
            <tr>
              <th>REF#</th>
              <th>Date</th>
              <th>Member Name (ID)</th>
              <th>Univ. Roll No</th>
              <th>Book Title (Acc#)</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map(c => (
              <tr key={c.CollectionId}>
                <td>{c.CollectionId}</td>
                <td>{new Date(c.PaymentDate).toLocaleDateString()}</td>
                <td>{c.MemberName} ({c.MemberId})</td>
                <td>{c.RollNo}</td>
                <td>{c.BookTitle} ({c.AccessionNo})</td>
                <td style={{textAlign:'right'}}>₹{parseFloat(c.Amount).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="5" style={{textAlign:'right'}}><strong>TOTAL REVENUE:</strong></td>
              <td style={{textAlign:'right'}}><strong>₹{sortedData.reduce((acc, curr) => acc + parseFloat(curr.Amount), 0).toFixed(2)}</strong></td>
            </tr>
          </tfoot>
        </table>
        
        <div className="print-footer">
          <div>Report prepared by Librarian</div>
          <div>Authorized Signature</div>
        </div>
      </div>
    </div>
  );
};

export default Collection;
